import { EBroadbandTechnology, ICart, ICartAddon, IService } from "@features/cart/ICart";
import { EAppointmentSlot, EAppointmentType, EServiceGroup, EServiceTechnology } from "@constants";

export interface IUsageResponse {
    description: string;
    displayOrder: number;
}

export interface IOfferResponse {
    catalogCode: string;
    description: string;
    comment: string;
    displayOrder: number;
    amountVatIncluded: ICartAmountVatIncludedResponse;
    serviceGroup: EServiceGroup;
    usages: IUsageResponse[];
    broadbandTechnology: string | null;
}

export interface IPortinCartResponse {
    msisdn: string;
    portStartDateTime: string;
}

export interface IRecurringAmountResponse {
    amount: number;
    discountFrequency?: string;
    discountOccurrence?: number;
    discountedAmount?: number;
}

export interface IAmountResponse {
    recurringAmount: IRecurringAmountResponse;
}

export interface IAddressResponse {
    addressLine1: string;
    addressLine2?: string;
    addressLine3?: string;
    eirCode?: string;
    county: string;
    town: string;
}

export interface IPrequalServiceResponse {
    serviceDate?: Date;
    broadbandTechnology: EServiceTechnology;
    slotAvailable: boolean;
}

export interface IPrequalResponse {
    prequalId: string;
    addressResponse: IAddressResponse;
    onlinePrequalId?: string;
    services: IPrequalServiceResponse[];
}

export interface IAddonsResponse {
    id: number;
    code: string;
    catalogCode: string;
    description: string;
    displayOrder: number;
    amountVatIncluded: IAmountResponse;
    max: number;
}

export interface IAppointmentResponse {
    type: EAppointmentType;
    at?: Date;
    time?: EAppointmentSlot;
}

export interface ICartOfferResponse {
    id: number;
    amountVatIncluded: ICartAmountVatIncludedResponse;
    portInInfo?: IPortinCartResponse;
    baseOffer: IOfferResponse;
    inclusiveEquipments: IInclusiveEquipmentResponse[];
    prequal?: IPrequalResponse;
    appointment?: IAppointmentResponse;
    activationAt?: Date;
    addOns: IAddonsResponse[];
}

export interface IInclusiveEquipmentResponse {
    simCardTypes: EEirSalesSimType[];
}

export enum EEirSalesSimType {
    ESIM = "ESIM",
    PSIM = "ALL_IN_1",
}

export interface ICartAmountVatIncludedResponse {
    recurringAmount: IRecurringAmountResponse;
    upFrontAmount: number;
}

export interface IAdditionalAllowedOffer {
    number: number;
    serviceGroup: EServiceGroup;
}

export interface ICartResponse {
    numberAdditionalAllowedOffers: IAdditionalAllowedOffer[];
    amountVatIncluded: ICartAmountVatIncludedResponse;
    offers: ICartOfferResponse[];
}

export const getResponseToState = (response: ICartResponse): ICart => ({
    nbrAdditionalAllowedMobileSubscriptions:
        response.numberAdditionalAllowedOffers?.find(
            (numberAdditionalAllowedOffer) => numberAdditionalAllowedOffer.serviceGroup === EServiceGroup.MOBILE
        )?.number ?? 0,
    nbrAdditionalAllowedBroadbandSubscriptions:
        response.numberAdditionalAllowedOffers?.find(
            (numberAdditionalAllowedOffer) => numberAdditionalAllowedOffer.serviceGroup === EServiceGroup.SA_BB
        )?.number ?? 0,
    offers: response.offers
        .map((offer) => ({
            baseOffer: {
                catalogOfferCode: offer.baseOffer.catalogCode,
                description: offer.baseOffer.description,
                comment: offer.baseOffer.comment,
                displayOrder: offer.baseOffer.displayOrder,
                recurringChargeAmount: offer.baseOffer.amountVatIncluded?.recurringAmount?.amount,
                upFrontChargeAmount: offer.baseOffer.amountVatIncluded?.upFrontAmount,
                serviceGroup: offer.baseOffer.serviceGroup,
                usages: offer.baseOffer.usages.map((usage) => ({
                    description: usage.description,
                    displayOrder: usage.displayOrder,
                })),
                broadbandTechnology: (offer.baseOffer.broadbandTechnology as EBroadbandTechnology) ?? undefined,
            },
            addOns: offer.addOns.map((addOn) => mapAddOnResponseToCartAddOn(addOn)),
            offerId: offer.id,
            port: offer.portInInfo
                ? {
                      phoneNumber: offer.portInInfo.msisdn,
                      portStartDateTime: offer.portInInfo.portStartDateTime,
                  }
                : undefined,
            recurringChargeAmount: offer.amountVatIncluded?.recurringAmount?.amount,
            upFrontChargeAmount: offer.amountVatIncluded?.upFrontAmount,
            simType: offer.inclusiveEquipments.map((inclusiveEquipment) => inclusiveEquipment.simCardTypes).flat()[0],
            prequal: offer.prequal
                ? {
                      prequalId: offer.prequal.prequalId,
                      addressResponse: {
                          addressLine1: offer.prequal.addressResponse.addressLine1,
                          addressLine2: offer.prequal.addressResponse.addressLine2,
                          addressLine3: offer.prequal.addressResponse.addressLine3,
                          eircode: offer.prequal.addressResponse.eirCode,
                          county: offer.prequal.addressResponse.county,
                          town: offer.prequal.addressResponse.town,
                      },
                      onlinePrequalId: offer.prequal.onlinePrequalId ?? offer.prequal.prequalId,
                      isPreOrder: offer.prequal?.services?.[0]?.serviceDate
                          ? new Date(offer.prequal.services[0].serviceDate) > new Date()
                          : false,
                      services: [
                          ...(offer.prequal?.services?.map((service) => {
                              return {
                                  broadbandTechnology: service.broadbandTechnology,
                                  slotAvailable: service.slotAvailable,
                              } as IService;
                          }) ?? []),
                      ],
                      serviceDate: offer.prequal?.services?.[0]?.serviceDate
                          ? new Date(offer.prequal.services[0].serviceDate)
                          : undefined,
                  }
                : undefined,
            appointment: offer.appointment
                ? {
                      type: offer.appointment.type,
                      time: offer.appointment.time,
                      at: offer.appointment.at ? new Date(offer.appointment.at) : undefined,
                  }
                : undefined,
            activationAt: offer.activationAt ? new Date(offer.activationAt) : undefined,
        }))
        .sort((a, b) => a.baseOffer.displayOrder - b.baseOffer.displayOrder),
    recurringChargeAmount: response.amountVatIncluded?.recurringAmount?.amount,
    upFrontChargeAmount: response.amountVatIncluded?.upFrontAmount,
});

const mapAddOnResponseToCartAddOn = (addOn: IAddonsResponse): ICartAddon => ({
    id: addOn.id,
    catalogCode: addOn.catalogCode,
    description: addOn.description,
    amountVatIncluded: addOn.amountVatIncluded,
});
